import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "@/lib/utils";

const brandInputVariants = cva(
	"flex h-10 w-full border bg-background px-3 py-2 font-mono text-base text-foreground placeholder:text-muted-foreground/70 transition-colors outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:border-destructive",
	{
		variants: {
			variant: {
				default: "border-border focus-visible:border-brand",
				error: "border-destructive focus-visible:border-destructive",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

export interface BrandInputProps
	extends React.InputHTMLAttributes<HTMLInputElement>,
		VariantProps<typeof brandInputVariants> {
	icon?: React.ReactNode;
	ref?: React.Ref<HTMLInputElement>;
}

function BrandInput({
	className,
	variant,
	icon,
	type,
	ref,
	...props
}: BrandInputProps) {
	if (icon) {
		return (
			<div className="relative">
				<div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
					{icon}
				</div>
				<input
					type={type}
					className={cn(brandInputVariants({ variant }), "pl-10", className)}
					ref={ref}
					{...props}
				/>
			</div>
		);
	}

	return (
		<input
			type={type}
			className={cn(brandInputVariants({ variant, className }))}
			ref={ref}
			{...props}
		/>
	);
}

export interface BrandInputGroupProps
	extends React.HTMLAttributes<HTMLDivElement> {
	label?: string;
	error?: string;
	required?: boolean;
	ref?: React.Ref<HTMLDivElement>;
}

function BrandInputGroup({
	className,
	label,
	error,
	required,
	children,
	ref,
	...props
}: BrandInputGroupProps) {
	return (
		<div
			ref={ref}
			className={cn("flex flex-col gap-1.5", className)}
			{...props}
		>
			{label && (
				<label className="block font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					{label}
					{required && <span className="ml-0.5 text-red-500">*</span>}
				</label>
			)}
			{children}
			{error && (
				<p className="font-mono text-sm uppercase tracking-[0.14em] text-destructive">
					{error}
				</p>
			)}
		</div>
	);
}

export { BrandInput, BrandInputGroup };
